import React, { useEffect, useState } from 'react';
import { Trophy, Target, TrendingUp, Activity } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge
} from '../ui';
import { getJuegosByTorneo } from '../../lib/supabase';
import { parseLocalDate } from '../../lib/dateUtils';
import { cn, fixMojibake } from '../../lib/utils';

/**
 * EstadisticasTorneo - Muestra estadisticas resumidas de un torneo
 *
 * @param {object} torneo - Datos del torneo
 * @param {string|null} groupId - Grupo seleccionado para filtrar
 */
const EstadisticasTorneo = ({ torneo, groupId = null }) => {
  const [stats, setStats] = useState({
    totalJuegos: 0,
    juegosTerminados: 0,
    victorias: 0,
    derrotas: 0,
    empates: 0,
    puntosFavor: 0,
    puntosContra: 0,
    diferencia: 0
  });
  const [loading, setLoading] = useState(true);
  const [juegosList, setJuegosList] = useState([]);

  useEffect(() => {
    if (torneo?.id) {
      loadStats();
    }
  }, [torneo?.id, groupId]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const juegos = await getJuegosByTorneo(torneo.id) || [];
      const juegosFiltrados = groupId
        ? juegos.filter((j) => j.grupo_id === groupId)
        : juegos;
      setJuegosList(juegosFiltrados);

      const terminados = juegosFiltrados.filter((j) => j.estatus === 'terminado');

      let victorias = 0;
      let derrotas = 0;
      let empates = 0;
      let puntosFavor = 0;
      let puntosContra = 0;

      terminados.forEach((juego) => {
        if (juego.resultado === 'W') victorias++;
        else if (juego.resultado === 'L') derrotas++;
        else if (juego.resultado === 'D') empates++;

        if (juego.puntos_favor !== null) puntosFavor += juego.puntos_favor;
        if (juego.puntos_contra !== null) puntosContra += juego.puntos_contra;
      });

      setStats({
        totalJuegos: juegosFiltrados.length,
        juegosTerminados: terminados.length,
        victorias,
        derrotas,
        empates,
        puntosFavor,
        puntosContra,
        diferencia: puntosFavor - puntosContra
      });
    } catch (err) {
      console.error('Error loading tournament stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const winPercentage = stats.juegosTerminados > 0
    ? Math.round((stats.victorias / stats.juegosTerminados) * 100)
    : 0;

  const formatFecha = (value) => {
    if (!value) return '--';
    try {
      const parsed = parseLocalDate(value);
      return parsed ? format(parsed, "d 'de' MMM yyyy", { locale: es }) : value;
    } catch {
      return value;
    }
  };

  const FASE_LABELS = {
    grupal: 'Fase de grupos',
    octavos: 'Octavos',
    cuartos: 'Cuartos',
    semifinal: 'Semifinal',
    final: 'Final',
    tercer_lugar: 'Tercer lugar',
    otro: 'Otro'
  };

  const ESTATUS_LABELS = {
    programado: 'Programado',
    en_curso: 'En curso',
    terminado: 'Terminado',
    cancelado: 'Cancelado',
    pospuesto: 'Pospuesto'
  };

  const RESULTADO_STYLES = {
    W: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    L: 'bg-rose-50 text-rose-700 border-rose-200',
    D: 'bg-slate-50 text-slate-700 border-slate-200'
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Estadisticas del Torneo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 text-gray-500">
            Cargando estadisticas...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (stats.totalJuegos === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Estadisticas del Torneo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 text-gray-500">
            No hay juegos registrados aun
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between">
          <span>Estadisticas del Torneo</span>
          <Badge variant="secondary" className="text-xs">
            {stats.juegosTerminados} / {stats.totalJuegos} juegos completados
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-4">
          <StatCard
            icon={<Trophy className="h-5 w-5 text-amber-600" />}
            label="Record"
            value={`${stats.victorias}-${stats.derrotas}-${stats.empates}`}
            subtitle={`${winPercentage}% victorias`}
            color="amber"
          />

          <StatCard
            icon={<Target className="h-5 w-5 text-green-600" />}
            label="Puntos a favor"
            value={stats.puntosFavor}
            subtitle={stats.juegosTerminados > 0
              ? `${(stats.puntosFavor / stats.juegosTerminados).toFixed(1)} por juego`
              : '--'
            }
            color="green"
          />

          <StatCard
            icon={<Activity className="h-5 w-5 text-red-600" />}
            label="Puntos en contra"
            value={stats.puntosContra}
            subtitle={stats.juegosTerminados > 0
              ? `${(stats.puntosContra / stats.juegosTerminados).toFixed(1)} por juego`
              : '--'
            }
            color="red"
          />

          <StatCard
            icon={<TrendingUp className={cn(
              'h-5 w-5',
              stats.diferencia > 0 ? 'text-green-600' :
              stats.diferencia < 0 ? 'text-red-600' :
              'text-gray-600'
            )} />}
            label="Diferencia"
            value={stats.diferencia > 0 ? `+${stats.diferencia}` : stats.diferencia}
            subtitle={
              stats.diferencia > 0 ? 'Positiva' :
              stats.diferencia < 0 ? 'Negativa' :
              'Neutral'
            }
            color={
              stats.diferencia > 0 ? 'green' :
              stats.diferencia < 0 ? 'red' :
              'gray'
            }
          />
        </div>

        {stats.juegosTerminados > 0 && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Distribucion de resultados</span>
              <span className="text-xs text-gray-500">{stats.juegosTerminados} juegos</span>
            </div>
            <div className="flex h-3 rounded-full overflow-hidden bg-gray-100">
              {stats.victorias > 0 && (
                <div
                  className="bg-green-500"
                  style={{ width: `${(stats.victorias / stats.juegosTerminados) * 100}%` }}
                  title={`${stats.victorias} victorias`}
                />
              )}
              {stats.empates > 0 && (
                <div
                  className="bg-gray-400"
                  style={{ width: `${(stats.empates / stats.juegosTerminados) * 100}%` }}
                  title={`${stats.empates} empates`}
                />
              )}
              {stats.derrotas > 0 && (
                <div
                  className="bg-red-500"
                  style={{ width: `${(stats.derrotas / stats.juegosTerminados) * 100}%` }}
                  title={`${stats.derrotas} derrotas`}
                />
              )}
            </div>
            <div className="flex items-center justify-between mt-2 text-xs text-gray-600">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-green-500"></div>
                <span>{stats.victorias} V</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-gray-400"></div>
                <span>{stats.empates} E</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-red-500"></div>
                <span>{stats.derrotas} D</span>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h4 className="text-sm font-semibold text-gray-800">
              Juegos considerados ({juegosList.length})
            </h4>
            <span className="text-xs text-gray-500">Aplicando filtros actuales</span>
          </div>
          {juegosList.length === 0 ? (
            <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
              No hay juegos que coincidan con los filtros actuales.
            </div>
          ) : (
            <div className="space-y-3">
              {juegosList
                .slice()
                .sort((a, b) => (parseLocalDate(a.fecha) || new Date()) - (parseLocalDate(b.fecha) || new Date()))
                .map((juego) => {
                  const grupoNombre = fixMojibake(juego.grupo?.nombre_del_grupo || 'Grupo');
                  const rivalNombre = fixMojibake(juego.equipo?.nombre || juego.equipo_nombre || 'Rival');
                  const faseKey = (juego.fase || '').toLowerCase();
                  const tipoJuego = FASE_LABELS[faseKey] || fixMojibake(juego.fase || 'Juego');
                  const estatusKey = (juego.estatus || 'programado').toLowerCase();
                  const marcadorDisponible =
                    juego.puntos_favor !== null && juego.puntos_contra !== null;
                  const marcadorLabel = marcadorDisponible
                    ? `${juego.puntos_favor} - ${juego.puntos_contra}`
                    : 'Pendiente';
                  return (
                    <div
                      key={juego.id}
                      className="rounded-lg border bg-white p-3 sm:p-4 shadow-sm"
                    >
                      <div className="sm:hidden">
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{formatFecha(juego.fecha)}</span>
                          <Badge variant="outline" className="text-[10px]">
                            {ESTATUS_LABELS[estatusKey] || fixMojibake(juego.estatus || 'Programado')}
                          </Badge>
                        </div>
                        <div className="mt-1 text-sm font-semibold">
                          {grupoNombre} vs {rivalNombre}
                        </div>
                        <div className="mt-1 flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">{tipoJuego}</span>
                          <span className="font-semibold">{marcadorLabel}</span>
                        </div>
                        {juego.resultado && (
                          <div className="mt-1">
                            <Badge
                              variant="outline"
                              className={cn('text-[10px]', RESULTADO_STYLES[juego.resultado])}
                            >
                              {juego.resultado}
                            </Badge>
                          </div>
                        )}
                      </div>

                      <div className="hidden sm:grid gap-3 md:grid-cols-[140px,1fr,160px]">
                        <div>
                          <div className="text-xs text-muted-foreground">Fecha</div>
                          <div className="text-sm font-semibold">{formatFecha(juego.fecha)}</div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {ESTATUS_LABELS[estatusKey] || fixMojibake(juego.estatus || 'Programado')}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Tipo de juego</div>
                          <div className="text-sm font-semibold">{tipoJuego}</div>
                          <div className="mt-2 flex flex-wrap gap-2 text-xs">
                            <Badge variant="secondary">{grupoNombre}</Badge>
                            <Badge variant="outline">vs {rivalNombre}</Badge>
                          </div>
                        </div>
                        <div className="flex items-center justify-start md:justify-end">
                          <div className="text-right">
                            <div className="text-xs text-muted-foreground">Marcador</div>
                            <div className="text-lg font-semibold">{marcadorLabel}</div>
                            {juego.resultado && (
                              <Badge
                                variant="outline"
                                className={cn('mt-1 text-xs', RESULTADO_STYLES[juego.resultado])}
                              >
                                {juego.resultado}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const StatCard = ({ icon, label, value, subtitle, color = 'gray' }) => {
  const colorClasses = {
    amber: 'border-amber-100 bg-amber-50',
    green: 'border-green-100 bg-green-50',
    red: 'border-red-100 bg-red-50',
    gray: 'border-gray-100 bg-gray-50'
  };

  return (
    <div
      className={cn(
        'rounded-lg border p-3 sm:p-4',
        colorClasses[color] || colorClasses.gray
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] sm:text-xs uppercase tracking-wide text-gray-600 mb-1">
            {label}
          </p>
          <p className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{value}</p>
          {subtitle && (
            <p className="text-[10px] sm:text-xs text-gray-600 mt-1 truncate">{subtitle}</p>
          )}
        </div>
        <div className="flex-shrink-0">
          <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-white/70 flex items-center justify-center shadow">
            {icon}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EstadisticasTorneo;
